import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { ErroreApi } from '../api/cliente.js';
import { peso } from '../api/documenti.js';
import {
  type AttivitaTrattativa,
  type DocumentoTrattativa,
  type PropostaListino,
  indirizzoAllegato,
  indirizzoStampaProposta,
  useAllegaFileTrattativa,
  useCambiaStatoProposta,
  useGeneraListino,
  useOpzioniTrattative,
  useSalvaDocumentoTrattativa,
  useTogliAttivita,
  useTogliDocumentoTrattativa,
  useTrattativa,
} from '../api/trattative.js';
import { Dato } from '../componenti/Dato.js';
import { ZonaFile } from '../componenti/ZonaFile.js';
import { data, dataOra, giorno } from '../formati.js';
import { ModaleAttivita } from './ModaleAttivita.js';
import { ModaleProposta } from './ModaleProposta.js';
import { ModaleTrattativa } from './ModaleTrattativa.js';
import { STATI } from './Trattative.js';
import { t } from '../i18n/lingua.js';

/**
 * La scheda di una trattativa.
 *
 * Nel v2 sono quattro schede in cima — dati, attività, documenti, listini —
 * e per capire a che punto si è bisogna aprirle una per una. Qui la storia
 * (le attività) sta accanto ai dati, perché è la cosa che si guarda per
 * prima, e le proposte di listino hanno i loro pulsanti di stato in chiaro
 * invece che una tendina con dentro otto valori tecnici.
 */

const SCHEDE = [
  { chiave: 'principale', titolo: t('Dati e attività') },
  { chiave: 'proposte', titolo: t('Proposte di listino') },
  { chiave: 'documenti', titolo: t('Documenti') },
] as const;

type Scheda = (typeof SCHEDE)[number]['chiave'];

const euro = (valore: number | null, valuta = 'EUR') =>
  valore === null
    ? null
    : valore.toLocaleString('it-IT', {
        style: 'currency',
        currency: valuta,
        maximumFractionDigits: 0,
      });

export function Trattativa({ id }: { id: number }) {
  const dati = useTrattativa(id);
  const opzioni = useOpzioniTrattative();
  const cambiaStato = useCambiaStatoProposta(id);
  const generaListino = useGeneraListino(id);
  const togliAttivita = useTogliAttivita(id);
  const salvaDocumento = useSalvaDocumentoTrattativa(id);
  const togliDocumento = useTogliDocumentoTrattativa(id);
  const allega = useAllegaFileTrattativa(id);

  const [scheda, setScheda] = useState<Scheda>('principale');
  const [modificaAperta, setModificaAperta] = useState(false);
  const [attivitaAperta, setAttivitaAperta] = useState<AttivitaTrattativa | null | undefined>(
    undefined,
  );
  const [propostaAperta, setPropostaAperta] = useState<PropostaListino | null | undefined>(
    undefined,
  );
  const [nuovoDocumento, setNuovoDocumento] = useState('');

  if (dati.isLoading) return <div className="page-head">{t('Un momento…')}</div>;
  if (dati.error || !dati.data) {
    return (
      <div className="page-head">
        <div>
          <Link className="back-link" to="/negotiations">
            {t('← Torna alle trattative')}
          </Link>
          <h1 className="page-title">{t('Trattativa non trovata')}</h1>
        </div>
      </div>
    );
  }

  const tr = dati.data;
  const stato = STATI[tr.status] ?? { testo: tr.status, classe: '' };
  const pesato =
    tr.budgetAmount === null ? null : (tr.budgetAmount * tr.probabilityClosingDeal) / 100;
  const etichettaAttivita = (tipo: string) =>
    opzioni.data?.activityTypes.find((x) => x.value === tipo);
  const etichettaProposta = (s: string) =>
    opzioni.data?.proposalStates.find((x) => x.value === s);

  // anche togliere un documento o un'attività può non riuscire: prima non
  // lo diceva nessuno, e il documento sembrava tolto
  const erroreAzione =
    [cambiaStato.error, generaListino.error, allega.error, togliDocumento.error, togliAttivita.error]
      .map((e) =>
        e instanceof ErroreApi ? e.message : e ? t('L’operazione non è riuscita: riprova.') : null,
      )
      .find(Boolean) ?? null;

  return (
    <>
      <div className="page-head">
        <div>
          <Link className="back-link" to="/negotiations">
            {t('← Torna alle trattative')}
          </Link>
          <h1 className="page-title">{tr.subject}</h1>
          <div className="page-sub">
            <Link className="cell-link" to="/customers/$id" params={{ id: String(tr.customerId) }}>
              {tr.customer}
            </Link>
            {tr.customerBcCode && <span className="dim num"> {tr.customerBcCode}</span>}
            {tr.expectedClosingDate && <> · {t('chiusura prevista {data}', { data: giorno(tr.expectedClosingDate) })}</>}
            {tr.archived && (
              <span className="badge" style={{ marginLeft: 8 }}>
                {t('archiviata')}
              </span>
            )}
          </div>
        </div>
        <div className="page-actions">
          <span className={`badge ${stato.classe}`}>{stato.testo}</span>
          {tr.budgetAmount !== null && (
            <span className="num" style={{ fontSize: 18, fontWeight: 600 }}>
              {euro(tr.budgetAmount)}
            </span>
          )}
          <button className="btn" onClick={() => setModificaAperta(true)}>
            {t('Modifica')}
          </button>
        </div>
      </div>

      {erroreAzione && (
        <div className="avviso" role="alert" style={{ margin: '0 20px 12px' }}>
          <span className="avviso-segno">▲</span>
          <span>{erroreAzione}</span>
        </div>
      )}

      <div className="tabs">
        {SCHEDE.map((s) => (
          <button
            key={s.chiave}
            className={scheda === s.chiave ? 'tab tab-on' : 'tab'}
            onClick={() => setScheda(s.chiave)}
          >
            {s.titolo}
            {s.chiave === 'proposte' && tr.pricelists.length > 0 && (
              <span className="count">{tr.pricelists.length}</span>
            )}
            {s.chiave === 'documenti' && tr.documents.length > 0 && (
              <span className="count">{tr.documents.length}</span>
            )}
          </button>
        ))}
      </div>

      {scheda === 'principale' && (
        <div className="grid grid-2" style={{ margin: '14px 20px 24px' }}>
          <div className="card">
            <div className="card-head">
              <span className="card-title">{t('La trattativa')}</span>
            </div>
            <div className="card-body">
              <div className="fields">
                <Dato etichetta="Budget" mono>
                  {euro(tr.budgetAmount)}
                </Dato>
                <Dato
                  etichetta="Valore atteso"
                  nota={t(' — budget per la probabilità')}
                  mono
                >
                  {euro(pesato)}
                </Dato>
                <Dato etichetta="Probabilità di chiusura" pieno>
                  <span className="row" style={{ gap: 8 }}>
                    <span className="barra-probabilita barra-larga" aria-hidden="true">
                      <span style={{ width: `${tr.probabilityClosingDeal}%` }} />
                    </span>
                    <span className="num">{tr.probabilityClosingDeal}%</span>
                  </span>
                </Dato>
                <Dato etichetta="Venditore">{tr.salesman}</Dato>
                <Dato etichetta="Agente">{tr.agent}</Dato>
                <Dato etichetta="Esigenze del cliente" pieno>
                  {tr.customerNeeds}
                </Dato>
                <Dato etichetta="Note" pieno>
                  {tr.notes}
                </Dato>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <span className="card-title">{t('Attività')}</span>
              <button
                className="btn btn-primary"
                style={{ marginLeft: 'auto' }}
                onClick={() => setAttivitaAperta(null)}
              >
                {t('Registra un’attività')}
              </button>
            </div>
            <div className="card-body">
              {tr.activities.length === 0 ? (
                <p className="dim" style={{ margin: 0, fontSize: 13 }}>
                  {t('Ancora niente. La prima telefonata, la prima e-mail: è la storia della trattativa, e serve a chi la riprende in mano fra un mese.')}
                </p>
              ) : (
                <ul className="cronologia">
                  {tr.activities.map((a) => (
                    <li key={a.id}>
                      <span className="cronologia-segno" title={etichettaAttivita(a.activityType)?.it}>
                        {etichettaAttivita(a.activityType)?.segno ?? '•'}
                      </span>
                      <div className="cronologia-corpo">
                        <div className="row" style={{ gap: 8, alignItems: 'baseline' }}>
                          <strong>{a.description}</strong>
                          <span className="dim num" style={{ fontSize: 12 }}>
                            {dataOra(a.activityDate)}
                          </span>
                          <span className="row" style={{ gap: 4, marginLeft: 'auto' }}>
                            <button
                              className="btn btn-ghost"
                              onClick={() => setAttivitaAperta(a)}
                            >
                              {t('Modifica')}
                            </button>
                            <button
                              className="btn btn-ghost"
                              onClick={() => togliAttivita.mutate(a.id)}
                            >
                              {t('Togli')}
                            </button>
                          </span>
                        </div>
                        {a.extendedDescription && (
                          <p className="dim" style={{ margin: '4px 0 0', fontSize: 13 }}>
                            {a.extendedDescription}
                          </p>
                        )}
                        {a.attachments.length > 0 && (
                          <ul className="doclist" style={{ marginTop: 6 }}>
                            {a.attachments.map((f) => (
                              <li key={f.id}>
                                <a
                                  className="cell-link"
                                  href={indirizzoAllegato(tr.id, 'activity', f.id)}
                                  download={f.fileName}
                                >
                                  {f.fileName}
                                </a>
                                <span className="dim">{peso(f.size)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {scheda === 'proposte' && (
        <div style={{ margin: '14px 20px 24px' }}>
          <div className="card">
            <div className="card-head">
              <span className="card-title">{t('Proposte di listino')}</span>
              <button
                className="btn btn-primary"
                style={{ marginLeft: 'auto' }}
                onClick={() => setPropostaAperta(null)}
              >
                {t('Nuova proposta')}
              </button>
            </div>
            <div className="card-body">
              {tr.pricelists.length === 0 ? (
                <p className="dim" style={{ margin: 0, fontSize: 13 }}>
                  {t('Nessuna proposta. Una proposta è un listino che non è ancora ufficiale: quando viene approvata si trasforma in un listino vero.')}
                </p>
              ) : (
                <div className="proposte">
                  {tr.pricelists.map((p) => {
                    const s = etichettaProposta(p.status);
                    // approvare e respingere li fa un responsabile: al
                    // commerciale quei due passaggi non si offrono
                    const prossimi = (opzioni.data?.proposalTransitions[p.status] ?? []).filter(
                      (x) =>
                        opzioni.data?.canApprove || (x !== 'approved' && x !== 'not_approved'),
                    );
                    return (
                      <div className="proposta" key={p.id}>
                        <div className="row" style={{ gap: 10, alignItems: 'baseline' }}>
                          <strong>{p.name}</strong>
                          <span className="badge">{s?.it ?? p.status}</span>
                          <span className="dim num" style={{ fontSize: 12 }}>
                            {p.currency} · {p.shippingMethod.toUpperCase()} ·{' '}
                            {p.articles === 1 ? t('1 articolo') : t('{n} articoli', { n: p.articles })}
                          </span>
                          <span className="dim" style={{ fontSize: 12, marginLeft: 'auto' }}>
                            {giorno(p.startDate)} — {giorno(p.endDate)}
                          </span>
                        </div>

                        {s?.nota && (
                          <p className="dim" style={{ margin: '4px 0 0', fontSize: 12 }}>
                            {s.nota}
                          </p>
                        )}

                        {p.approved && p.approvedAt && (
                          <p className="dim" style={{ margin: '4px 0 0', fontSize: 12 }}>
                            {t('Approvata da {chi} il {quando}.', { chi: p.approvedBy ?? t('qualcuno'), quando: dataOra(p.approvedAt) })}
                          </p>
                        )}

                        {p.officialPricelistId && (
                          <p className="nota-prezzo" style={{ marginTop: 8 }}>
                            {t('Da questa proposta è nato il listino')}{' '}
                            <Link
                              className="cell-link strong"
                              to="/price-lists/$id"
                              params={{ id: String(p.officialPricelistId) }}
                            >
                              {p.officialPricelistName}
                            </Link>
                            {t('. I prezzi sono in vigore: gli ordini di questo cliente li trovano da soli.')}
                          </p>
                        )}

                        <div className="row" style={{ gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                          <button className="btn" onClick={() => setPropostaAperta(p)}>
                            {t('Apri e modifica i prezzi')}
                          </button>

                          {/* il documento che va al cliente: stesso foglio
                              del v2, perché il cliente l'ha già visto */}
                          <a
                            className="btn"
                            href={indirizzoStampaProposta(tr.id, p.id)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t('Stampa PDF')}
                          </a>

                          {prossimi.map((verso) => {
                            const e = etichettaProposta(verso);
                            return (
                              <button
                                key={verso}
                                className="btn"
                                disabled={cambiaStato.isPending}
                                onClick={() =>
                                  cambiaStato.mutate({ propostaId: p.id, status: verso })
                                }
                              >
                                {e?.it ?? verso}
                              </button>
                            );
                          })}

                          {p.canGenerate && (
                            <button
                              className="btn btn-primary"
                              disabled={generaListino.isPending}
                              onClick={() => generaListino.mutate(p.id)}
                            >
                              {generaListino.isPending
                                ? 'Genero…'
                                : t('Genera il listino ufficiale')}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {scheda === 'documenti' && (
        <div style={{ margin: '14px 20px 24px' }}>
          <div className="card">
            <div className="card-head">
              <span className="card-title">{t('Documenti della trattativa')}</span>
            </div>
            <div className="card-body">
              <div className="row" style={{ gap: 8, marginBottom: 14 }}>
                <input
                  className="input"
                  placeholder={t('Descrizione del documento — «Visura camerale cliente»')}
                  value={nuovoDocumento}
                  onChange={(e) => setNuovoDocumento(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && nuovoDocumento.trim()) {
                      salvaDocumento.mutate(
                        { id: null, description: nuovoDocumento.trim() },
                        { onSuccess: () => setNuovoDocumento('') },
                      );
                    }
                  }}
                />
                <button
                  className="btn btn-primary"
                  disabled={nuovoDocumento.trim() === '' || salvaDocumento.isPending}
                  onClick={() =>
                    salvaDocumento.mutate(
                      { id: null, description: nuovoDocumento.trim() },
                      { onSuccess: () => setNuovoDocumento('') },
                    )
                  }
                >
                  {t('Aggiungi')}
                </button>
              </div>

              {tr.documents.length === 0 ? (
                <p className="dim" style={{ margin: 0, fontSize: 13 }}>
                  {t('Nessun documento. Qui vanno le carte della trattativa — visure, capitolati, bozze — che non sono ancora documenti dell\'archivio generale.')}
                </p>
              ) : (
                <div className="proposte">
                  {tr.documents.map((d) => (
                    <DocumentoRiga
                      key={d.id}
                      trattativaId={tr.id}
                      documento={d}
                      estensioni={opzioni.data ? undefined : undefined}
                      massimo={opzioni.data?.maxUploadBytes}
                      // «Sto caricando…» solo sul documento che sta caricando, non su tutti
                      inCorso={
                        allega.isPending &&
                        allega.variables?.tipo === 'documents' &&
                        allega.variables.id === d.id
                      }
                      onFile={(file) => allega.mutate({ tipo: 'documents', id: d.id, file })}
                      onTogli={() => togliDocumento.mutate(d.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {modificaAperta && (
        <ModaleTrattativa trattativa={tr} onChiudi={() => setModificaAperta(false)} />
      )}

      {attivitaAperta !== undefined && (
        <ModaleAttivita
          trattativaId={tr.id}
          // l'attività com'è adesso, non come era quando si è aperta la
          // finestra: un file appena caricato si vedeva solo riaprendola
          attivita={
            attivitaAperta === null
              ? null
              : (tr.activities.find((a) => a.id === attivitaAperta.id) ?? attivitaAperta)
          }
          onChiudi={() => setAttivitaAperta(undefined)}
        />
      )}

      {propostaAperta !== undefined && (
        <ModaleProposta
          trattativaId={tr.id}
          proposta={propostaAperta}
          onChiudi={() => setPropostaAperta(undefined)}
        />
      )}
    </>
  );
}

/** Una riga di documento con la sua zona di trascinamento. */
function DocumentoRiga({
  trattativaId,
  documento,
  massimo,
  inCorso,
  onFile,
  onTogli,
}: {
  trattativaId: number;
  documento: DocumentoTrattativa;
  estensioni?: string[];
  massimo?: number;
  inCorso: boolean;
  onFile: (file: File) => void;
  onTogli: () => void;
}) {
  return (
    <div className="proposta">
      <div className="row" style={{ gap: 10, alignItems: 'baseline' }}>
        <strong>{documento.description ?? 'Senza descrizione'}</strong>
        <span className="dim num" style={{ fontSize: 12 }}>
          {data(documento.createdAt)}
        </span>
        <button className="btn btn-ghost" style={{ marginLeft: 'auto' }} onClick={onTogli}>
          {t('Togli')}
        </button>
      </div>

      {documento.attachments.length > 0 && (
        <ul className="doclist" style={{ marginTop: 6 }}>
          {documento.attachments.map((f) => (
            <li key={f.id}>
              <a
                className="cell-link"
                href={indirizzoAllegato(trattativaId, 'document', f.id)}
                download={f.fileName}
              >
                {f.fileName}
              </a>
              <span className="dim">{peso(f.size)}</span>
            </li>
          ))}
        </ul>
      )}

      <div style={{ marginTop: 10 }}>
        <ZonaFile file={null} massimo={massimo} onFile={(file) => file && onFile(file)} />
        {inCorso && (
          <p className="dim" style={{ fontSize: 12, marginBottom: 0 }}>
            {t('Sto caricando…')}
          </p>
        )}
      </div>
    </div>
  );
}
